/**
 * The quality of a stored voice sample (ADR-0062), as the server judged it once the voiceprint
 * was made: a grade, the reasons that lowered it, the speech it measured, and whether the sample
 * is good enough to stand as a reference. The server measures the audio itself — with a speech
 * detector, the distance from the room's noise and a one-voice check — so what the phone counted
 * while it recorded gives way to this. The grades are provisional until they are tuned on real
 * recordings; the screens say so once. Pure.
 *
 * Not to be confused with `VoiceQualityView` (`voiceprint-types.ts`), which the voice matching
 * shows of a voiceprint.
 */

import type { TFunction } from 'i18next'
import { BadgeCheck, CircleCheck, type LucideIcon, TriangleAlert } from 'lucide-react'

/** `good`: nothing lowered it. `ok`: usable, with reasons. `low`: weak — to be recorded again. */
export type VoiceSampleGrade = 'good' | 'ok' | 'low'

export const VOICE_SAMPLE_GRADES: readonly VoiceSampleGrade[] = ['good', 'ok', 'low']

/** Why a sample was graded down. */
export type VoiceSampleFlag =
  | 'too_short'
  | 'noisy'
  | 'clipped'
  | 'quiet'
  | 'narrowband'
  | 'second_voice'

/** Every reason the workspace has words for, in the order they are said. */
export const VOICE_SAMPLE_FLAGS: readonly VoiceSampleFlag[] = [
  'second_voice',
  'too_short',
  'noisy',
  'clipped',
  'quiet',
  'narrowband',
]

/** What the server says of a stored sample's quality. */
export interface VoiceSampleQuality {
  grade: VoiceSampleGrade
  /** The reasons, in the order they are said; one the workspace has no words for is left out. */
  flags: readonly VoiceSampleFlag[]
  /** The net speech the server measured in the sample; null where it does not say. */
  speechMs: number | null
  /** The decoded recording length; absent when an older server does not provide it. */
  durationMs?: number | null
  /** The sample is good enough to stand as a reference of the subject's voice. */
  reference: boolean
}

/** Whether another recording could give a better sample: offered for every grade but `good`. */
export const mayRecordBetter = (quality: VoiceSampleQuality | null | undefined): boolean =>
  quality != null && quality.grade !== 'good'

/** Whether two answers say the same: the page hands a new object over at every poll. */
export function sameQuality(
  a: VoiceSampleQuality | null | undefined,
  b: VoiceSampleQuality | null | undefined
): boolean {
  if (!a || !b) return (a ?? null) === (b ?? null)
  return (
    a.grade === b.grade &&
    a.reference === b.reference &&
    a.speechMs === b.speechMs &&
    (a.durationMs ?? null) === (b.durationMs ?? null) &&
    a.flags.join() === b.flags.join()
  )
}

/** The icon and the icon's tone of a grade: drawn beside the grade in words, never instead. */
export const GRADE_LOOK: Record<VoiceSampleGrade, { Icon: LucideIcon; tone: string }> = {
  good: { Icon: BadgeCheck, tone: 'text-primary-strong' },
  ok: { Icon: CircleCheck, tone: 'text-foreground' },
  low: { Icon: TriangleAlert, tone: 'text-warning-foreground' },
}

const COPY = 'humint.voice.sample.verdict'

/**
 * Between two reasons, and between the grade and its reasons: the dot keeps to the end of its
 * line, so a list that breaks never starts a line with it.
 */
export const VERDICT_SEPARATOR = '\u00A0· '

/** The grade in words: *Excellent sample*, *Usable sample*, *Weak sample*. */
export const gradeText = (grade: VoiceSampleGrade, t: TFunction): string =>
  t(`${COPY}.grade.${grade}`)

/** The reasons in words, one after the other; empty for a sample nothing lowered. */
export function reasonsText(quality: Pick<VoiceSampleQuality, 'flags'>, t: TFunction): string {
  return quality.flags.map(flag => t(`${COPY}.reasons.${flag}`)).join(VERDICT_SEPARATOR)
}

/** The grade and its reasons in one line: for a row that has one line for them. */
export function verdictLine(quality: VoiceSampleQuality, t: TFunction): string {
  const reasons = reasonsText(quality, t)
  const grade = gradeText(quality.grade, t)
  return reasons ? `${grade}${VERDICT_SEPARATOR}${reasons}` : grade
}
